import { ConstantItems, getDefaultConfig } from "@constants/shared";
import { OptionType } from "@models/clients/optionType";
import bugsnagClient from "@services/bugsnag";
import { ClientDetails } from "@models/clients/clientDetailsModel";
import * as _ from "lodash";

export class ClientCRMRepo {
  static $inject = ["$http", "config"];
  private bugsnag: typeof bugsnagClient;

  constructor(
    private $http: angular.IHttpService,
    private constants: ConstantItems,
  ) {}

  private get config(): ng.IRequestShortcutConfig {
    return getDefaultConfig();
  }

  getBankOpenings() {
    return this.$http.get(`${this.constants.clientCRMUrl}/bankOpenings`, this.config)
  }

  getProfessionalActivities() {
    return this.$http.get(`${this.constants.clientCRMUrl}/professionalActivities`, this.config)
  }

  deleteProfessionalActivities(itemIds: number[]) {
    return this.$http.delete(`${this.constants.clientCRMUrl}/professionalActivities`, { params: { itemIds: itemIds } })
  }

  deleteBankOpenings(itemIds: number[]) {
    return this.$http.delete(`${this.constants.clientCRMUrl}/bankOpenings`, { params: { itemIds: itemIds } })
  }

  saveProfessionalActivity(professionalActivity: any) {
    return this.$http.post(`${this.constants.clientCRMUrl}/professionalActivities`, professionalActivity, this.config)
  }

  saveBank(bank: any) {
    return this.$http.post(`${this.constants.clientCRMUrl}/bankOpenings`, bank, this.config)
  }

  saveClient(client: ClientDetails) {
    return this.$http.post(`${this.constants.clientCRMUrl}/client`, client, this.config)
  }

  getOptions(optionType: string) {
    return this.$http.get(`${this.constants.clientCRMUrl}/options/${optionType}`, this.config)
  }

  getAllOptions() {
    // Get all options without needing to pass option types
    return this.$http.get(`${this.constants.clientCRMUrl}/options/bulk`, this.config)
  }

  /**
   * Get the options cache version from the backend. Used to invalidate local cache when options change.
   * Response: { version: string | null } (ISO date string, or null if not yet set).
   */
  getOptionsVersion() {
    return this.$http.get<{ version: string | null }>(`${this.constants.clientCRMUrl}/options/version`, this.config)
  }

  getUsers() {
    return this.$http.get(`${this.constants.clientCRMUrl}/users`, this.config)
  }

  getCurrencies() {
    return this.$http.get(`${this.constants.clientCRMUrl}/currencies`, this.config)
  }

  extractDynamicsRiskMatrixPep() {
    return this.$http.get(`${this.constants.clientCRMUrl}/extractDynamicsRiskMatrixPep`, this.config)
  }

  deleteCurrencies(itemIds: number[]) {
    return this.$http.delete(`${this.constants.clientCRMUrl}/currencies`, { params: { itemIds: itemIds } })
  }

  saveCurrency(currency: any) {
    return this.$http.post(`${this.constants.clientCRMUrl}/currencies`, currency, this.config)
  }

  // Standardized methods for options management
  saveOption(optionType: string, option: any) {
    return this.$http.post(`${this.constants.clientCRMUrl}/options/${optionType}`, option, this.config)
  }

  deleteOptions(optionType: string, optionIds: number[]) {
    return this.$http.delete(`${this.constants.clientCRMUrl}/options/${optionType}`, { 
      params: { itemIds: optionIds },
      ...this.config 
    })
  }

  // Email Address methods
  getEmailAddresses(clientNumber: string) {
    return this.$http.get(`${this.constants.clientCRMUrl}/emailAddresses?clientNumber=${clientNumber}`, this.config)
  }

  saveEmailAddress(clientId: number, emailAddress: any) {
    console.log('saveEmailAddresses', clientId, emailAddress);
    return this.$http.post(`${this.constants.clientCRMUrl}/emailAddresses?clientId=${clientId}`, emailAddress, this.config)
  }

  deleteEmailAddresses(itemIds: number[]) {
    return this.$http.delete(`${this.constants.clientCRMUrl}/emailAddresses`, { 
      params: { itemIds: itemIds },
      ...this.config 
    })
  }

  // Previous Professional Activities methods
  getPreviousProfessionalActivities(clientNumber: string) {
    return this.$http.get(`${this.constants.clientCRMUrl}/previousProfessionalActivities?clientNumber=${clientNumber}`, this.config)
  }

  savePreviousProfessionalActivity(clientId: number, previousProfessionalActivity: any) {
    return this.$http.post(`${this.constants.clientCRMUrl}/previousProfessionalActivities?clientId=${clientId}`, previousProfessionalActivity, this.config)
  }

  deletePreviousProfessionalActivities(itemIds: number[]) {
    return this.$http.delete(`${this.constants.clientCRMUrl}/previousProfessionalActivities`, { 
      params: { itemIds: itemIds },
      ...this.config 
    })
  }

  // Additional Profile Details methods
  getAdditionalProfileDetails(clientNumber: string) {
    return this.$http.get(`${this.constants.clientCRMUrl}/additionalProfileDetails?clientNumber=${clientNumber}`, this.config)
  }

  saveAdditionalProfileDetails(clientId: number, additionalProfileDetails: any) {
    return this.$http.post(`${this.constants.clientCRMUrl}/additionalProfileDetails?clientId=${clientId}`, additionalProfileDetails, this.config)
  }

  deleteAdditionalProfileDetails(itemIds: number[]) {
    return this.$http.delete(`${this.constants.clientCRMUrl}/additionalProfileDetails`, { 
      params: { itemIds: itemIds },
      ...this.config 
    })
  }

  // Transaction Activities methods
  getTransactionActivities(clientNumber: string) {
    return this.$http.get(`${this.constants.clientCRMUrl}/transactionActivities?clientNumber=${clientNumber}`, this.config)
  }

  saveTransactionActivity(clientId: number, transactionActivity: any) {
    return this.$http.post(`${this.constants.clientCRMUrl}/transactionActivities?clientId=${clientId}`, transactionActivity, this.config)
  }

  deleteTransactionActivities(itemIds: number[]) {
    return this.$http.delete(`${this.constants.clientCRMUrl}/transactionActivities`, { 
      params: { itemIds: itemIds },
      ...this.config 
    })
  }

  getClientPortfolioAccounts(clientNumber: string) {
    return this.$http.get(`${this.constants.clientCRMUrl}/clientPortfolioAccounts?clientNumber=${clientNumber}`, this.config)
  }

  // Management Fees methods
  getManagementFees(clientNumber: string) {
    return this.$http.get(`${this.constants.clientCRMUrl}/managementFees?clientNumber=${clientNumber}`, this.config)
  }

  saveManagementFee(clientId: number, managementFee: any) {
    return this.$http.post(`${this.constants.clientCRMUrl}/managementFees?clientId=${clientId}`, managementFee, this.config)
  }

  deleteManagementFees(itemIds: number[]) {
    return this.$http.delete(`${this.constants.clientCRMUrl}/managementFees`, { 
      params: { itemIds: itemIds },
      ...this.config 
    })
  }

  // Signatory Regimes methods
  getSignatoryRegimes(clientNumber: string) {
    return this.$http.get(`${this.constants.clientCRMUrl}/signatoryRegimes?clientNumber=${clientNumber}`, this.config)
  }

  saveSignatoryRegime(clientId: number, signatoryRegime: any) {
    console.log('saveSignatoryRegime', clientId, signatoryRegime);
    return this.$http.post(`${this.constants.clientCRMUrl}/signatoryRegimes?clientId=${clientId}`, signatoryRegime, this.config)
  }

  saveAuthorisedSignatory(signatoryRegimeId: number, authorisedSignatory: any) {
    console.log('saveAuthorisedSignatory', signatoryRegimeId, authorisedSignatory);
    return this.$http.post(`${this.constants.clientCRMUrl}/signatoryRegimes/${signatoryRegimeId}/authorisedSignatories`, authorisedSignatory, this.config)
  }

  saveBeneficiary(signatoryRegimeId: number, beneficiary: any) {
    console.log('saveBeneficiary', signatoryRegimeId, beneficiary);
    return this.$http.post(`${this.constants.clientCRMUrl}/signatoryRegimes/${signatoryRegimeId}/beneficiaries`, beneficiary, this.config)
  }

  deleteSignatoryRegimes(itemIds: number[]) {
    return this.$http.delete(`${this.constants.clientCRMUrl}/signatoryRegimes`, { 
      params: { itemIds: itemIds },
      ...this.config 
    })
  }

  // Authorised Signatories methods
  getAuthorisedSignatories(signatoryRegimeId: number) {
    return this.$http.get(`${this.constants.clientCRMUrl}/signatoryRegimes/${signatoryRegimeId}/authorisedSignatories`, this.config)
  }

  // Beneficiaries methods
  getBeneficiaries(signatoryRegimeId: number) {
    return this.$http.get(`${this.constants.clientCRMUrl}/signatoryRegimes/${signatoryRegimeId}/beneficiaries`, this.config)
  }

  deleteAuthorisedSignatories(itemIds: number[]) {
    return this.$http.delete(`${this.constants.clientCRMUrl}/authorisedSignatories`, { 
      params: { itemIds: itemIds },
      ...this.config 
    })
  }

  deleteBeneficiaries(itemIds: number[]) {
    return this.$http.delete(`${this.constants.clientCRMUrl}/beneficiaries`, { 
      params: { itemIds: itemIds },
      ...this.config 
    })
  }

  // Restrictions methods
  getRestrictions(clientNumber: string) {
    return this.$http.get(`${this.constants.clientCRMUrl}/restrictions?clientNumber=${clientNumber} `, this.config)
  }

  saveRestriction(clientId: number, restriction: any) {
    return this.$http.post(`${this.constants.clientCRMUrl}/restrictions?clientId=${clientId}`, restriction, this.config)
  }

  deleteRestrictions(itemIds: number[]) {
    return this.$http.delete(`${this.constants.clientCRMUrl}/restrictions`, { 
      params: { itemIds: itemIds },
      ...this.config 
    })
  }

  // Compliances methods
  getCompliances(clientId: number) {
    return this.$http.get(`${this.constants.clientCRMUrl}/compliances?clientId=${clientId}`, this.config)
  }

  saveCompliance(clientId: number, compliance: any) {
    return this.$http.post(`${this.constants.clientCRMUrl}/compliances?clientId=${clientId}`, compliance, this.config)
  }

  deleteCompliances(itemIds: number[]) {
    return this.$http.delete(`${this.constants.clientCRMUrl}/compliances`, { 
      params: { itemIds: itemIds },
      ...this.config 
    })
  }

  // Connections Contacts methods
  getConnectionsContacts(clientNumber: string) {
    return this.$http.get(`${this.constants.clientCRMUrl}/connectionsContacts?clientNumber=${clientNumber}`, this.config)
  }

  getAllConnectionsContacts() {
    return this.$http.get(`${this.constants.clientCRMUrl}/connectionsContacts/all`, this.config)
  }

  saveConnectionContact(clientId: number | null, connection: any) {
    const url = clientId 
      ? `${this.constants.clientCRMUrl}/connectionsContacts?clientId=${clientId}`
      : `${this.constants.clientCRMUrl}/connectionsContacts`;
    return this.$http.post(url, connection, this.config)
  }

  deleteConnectionsContacts(itemIds: number[]) {
    return this.$http.delete(`${this.constants.clientCRMUrl}/connectionsContacts`, { 
      params: { itemIds: itemIds },
      ...this.config 
    })
  }

  // PortfolioExternalAccount methods
  getPortfolioExternalAccounts(clientId: number) {
    return this.$http.get(`${this.constants.clientCRMUrl}/portfolioExternalAccounts?clientId=${clientId}`, this.config)
  }

  savePortfolioExternalAccount(clientId: number, portfolioExternalAccount: any) {
    return this.$http.post(`${this.constants.clientCRMUrl}/portfolioExternalAccounts?clientId=${clientId}`, portfolioExternalAccount, this.config)
  }

  deletePortfolioExternalAccounts(itemIds: number[]) {
    return this.$http.delete(`${this.constants.clientCRMUrl}/portfolioExternalAccounts`, { 
      params: { itemIds: itemIds },
      ...this.config 
    })
  }

}


// WEBPACK FOOTER //
// ./src/services/clients/clientCRM.repo.ts